import { toNumber } from 'lodash-es';
import { v1 as uuid } from 'uuid';

import { CustomerLedgerUpdateTypeEnum, NotificationVariants, type useGlobalToasts } from '@talos/kyoko';

import type { CustomerBalanceTransactionDrawerState } from '../types';

export interface ConfirmTransferButtonCopyParams {
  transferType?: CustomerLedgerUpdateTypeEnum;
  transferMethod: 'initiate' | 'manual';
  showOnlyConfirmTransferFlow: boolean;
}

export interface ConfirmTransferButtonCopy {
  testIdSuffix: string;
  label: string;
}

export function getConfirmTransferButtonCopy({
  transferType,
  transferMethod,
  showOnlyConfirmTransferFlow,
}: ConfirmTransferButtonCopyParams): ConfirmTransferButtonCopy {
  if (transferType === CustomerLedgerUpdateTypeEnum.Deposit) {
    return { testIdSuffix: 'deposit', label: 'Preview Deposit' };
  }

  if (transferType === CustomerLedgerUpdateTypeEnum.Set) {
    return { testIdSuffix: 'set', label: 'Preview Set' };
  }

  if (transferMethod === 'initiate') {
    return {
      testIdSuffix: 'transfer',
      label: showOnlyConfirmTransferFlow ? 'Initiate Transfer' : 'Preview Transfer',
    };
  }

  return {
    testIdSuffix: 'manual-transfer',
    label: showOnlyConfirmTransferFlow ? 'Initiate Manual Transfer' : 'Preview Manual Transfer',
  };
}

// Constants
// Note: CustomerLedgerUpdateTypeEnum.Set is intentionally excluded from TRANSFER_TYPES
// because it represents an internal system action, not a user-initiated transfer.
export const TRANSFER_TYPES = [CustomerLedgerUpdateTypeEnum.Deposit, CustomerLedgerUpdateTypeEnum.Withdraw];
export const FORCE_WITHDRAW_CONFIRM_TEXT = 'confirm';

// Utility Functions
export function getTransferTypeBadgeText(transferType: CustomerLedgerUpdateTypeEnum): string {
  switch (transferType) {
    case CustomerLedgerUpdateTypeEnum.Withdraw:
      return 'Withdrawal';
    case CustomerLedgerUpdateTypeEnum.Deposit:
      return 'Deposit';
    case CustomerLedgerUpdateTypeEnum.Set:
      return 'Set';
    default:
      return transferType;
  }
}

export function getInitialState(
  formDefaults?: Partial<CustomerBalanceTransactionDrawerState>
): CustomerBalanceTransactionDrawerState {
  return {
    externalID: uuid(),
    transferMethod: 'initiate',
    transferType: CustomerLedgerUpdateTypeEnum.Deposit,
    ...formDefaults,
  };
}

export function createVerifyPastedValueIsNumber(addToast: ReturnType<typeof useGlobalToasts>['add']) {
  return (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedValue = e.clipboardData.getData('text');
    const numberValue = toNumber(pastedValue);

    if (isNaN(numberValue)) {
      e.preventDefault();
      addToast({
        text: `Invalid Amount: ${pastedValue}`,
        variant: NotificationVariants.Negative,
      });
    }
  };
}
